import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { fetchJson } from "../lib/api";
import { apiBase } from "../lib/apiBase";

export function getLayoutFromReadiness(readiness) {
    if (!readiness || typeof readiness !== "object" || Array.isArray(readiness)) return [];
    const perCoin = readiness?.per_coin;
    const normalizedPerCoin =
        perCoin && typeof perCoin === "object" && !Array.isArray(perCoin) ? Object.values(perCoin) : [];
    const layout =
        readiness?.binance_account_layout ||
        readiness?.live_accounts ||
        readiness?.layout ||
        normalizedPerCoin ||
        [];
    return Array.isArray(layout) ? layout : [];
}

const reasonLabels = {
    binance_enabled_false: "Binance disabled for this configured coin",
    binance_disabled: "Binance disabled for this configured coin",
    policy_excluded: "Policy-excluded",
    missing_key: "Missing Binance API key",
    missing_secret: "Missing Binance API secret",
    missing_client: "Missing Binance client",
    client_missing: "Missing Binance client",
    client_not_ready: "Binance client not ready",
    live_wallet_disabled: "Live wallet not ready",
    wallet_not_ready: "Wallet not ready",
    live_flags_disabled: "Live trading flags are disabled",
    malformed_coin_readiness: "Unknown/malformed readiness",
};

function formatReason(reason) {
    if (!reason) return "Unknown/malformed readiness";
    const value = String(reason);
    if (reasonLabels[value]) return reasonLabels[value];
    if (value.startsWith("missing ")) return value.replace("missing", "Missing");
    if (value.startsWith("missing_binance_keys:")) return `Missing Binance keys for ${value.split(":").slice(1).join(":")}`;
    return value.replaceAll("_", " ");
}

function getCoinName(item) {
    return (item?.symbol || item?.coin_key || item?.slug || "Unknown").toString().toUpperCase();
}

function getItemReasons(item) {
    if (!item || typeof item !== "object" || Array.isArray(item)) return ["malformed_coin_readiness"];

    const reasons = Array.isArray(item.blocking_reasons) ? [...item.blocking_reasons] : [];
    const envKeys = item.env_keys_present && typeof item.env_keys_present === "object" ? item.env_keys_present : null;

    if (item.binance_enabled === false && !reasons.some((r) => ["binance_enabled_false", "binance_disabled"].includes(String(r)))) {
        reasons.push("binance_enabled_false");
    }
    if (item.policy_excluded === true && !reasons.includes("policy_excluded")) {
        reasons.push("policy_excluded");
    }
    if (envKeys) {
        if (envKeys.key === false && !reasons.some((r) => String(r).startsWith("missing ") || r === "missing_key")) reasons.push("missing_key");
        if (envKeys.secret === false && !reasons.some((r) => String(r).startsWith("missing ") || r === "missing_secret")) reasons.push("missing_secret");
    }
    if (item.client_ready === false || item.binance_client_ready === false) reasons.push("client_not_ready");
    if (item.client_present === false || item.binance_client_present === false) reasons.push("missing_client");
    if (item.wallet_ready === false || item.live_wallet_ready === false) reasons.push("wallet_not_ready");
    if (typeof item.can_trade_live !== "boolean") reasons.push("malformed_coin_readiness");

    return Array.from(new Set(reasons.filter(Boolean).map(String)));
}

function EnvBadge({ status = { key: false, secret: false } }) {
    const { key = false, secret = false } = status || {};
    const both = key && secret;
    const color = both ? "bg-green-900/60 text-green-100" : key || secret ? "bg-amber-900/60 text-amber-100" : "bg-red-900/60 text-red-100";
    const label = both ? "Key + Secret" : key || secret ? "Partial" : "Missing";
    return (
        <span className={`rounded-full px-3 py-1 text-xs font-medium ${color}`}>
            {label}
        </span>
    );
}

EnvBadge.propTypes = {
    status: PropTypes.shape({ key: PropTypes.bool, secret: PropTypes.bool }),
};


export default function LiveAccountLayoutPanel({ readiness = null, className = "" }) {
    const readinessObject =
        readiness && typeof readiness === "object" && !Array.isArray(readiness);
    const [layout, setLayout] = useState(() => getLayoutFromReadiness(readiness));
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(!readinessObject);

    useEffect(() => {
        if (readinessObject) {
            setLayout(getLayoutFromReadiness(readiness));
            setError(null);
            setLoading(false);
            return undefined;
        }
        let mounted = true;
        async function load() {
            try {
                const res = await fetchJson(apiBase("trading_mode_status"));
                if (!mounted) return;
                if (!res?.ok) {
                    setError("api_error");
                    setLayout([]);
                } else {
                    setLayout(getLayoutFromReadiness(res?.data?.live_readiness));
                    setError(null);
                }
            } catch {
                setError("api_error");
                setLayout([]);
            } finally {
                if (mounted) setLoading(false);
            }
        }
        load();
        return () => {
            mounted = false;
        };
    }, [readiness, readinessObject]);

    const readyCoins = useMemo(() => layout.filter((item) => item?.can_trade_live === true), [layout]);
    const enabledCoins = useMemo(() => layout.filter((item) => item?.binance_enabled === true), [layout]);

    if (loading) {
        return <div className={`rounded-xl border border-gray-800 bg-gray-900/70 p-4 ${className}`}>Loading live account layout…</div>;
    }

    if (error) {
        return (
            <div className={`rounded-xl border border-gray-800 bg-gray-900/70 p-4 ${className}`} data-testid="live-account-layout-error">
                <p className="text-sm text-amber-200">Live account mapping unavailable (API error). Check nebula-api logs.</p>
            </div>
        );
    }

    if (!layout || layout.length === 0) {
        return (
            <div className={`rounded-xl border border-gray-800 bg-gray-900/70 p-4 ${className}`} data-testid="live-account-layout-empty">
                <p className="text-sm text-amber-200">Live account status checks unavailable. Empty check-passing lists are treated as fail-closed, not success.</p>
            </div>
        );
    }

    return (
        <div className={`rounded-xl border border-gray-800 bg-gray-900/70 p-4 ${className}`} data-testid="live-account-layout">
            <div className="mb-3 flex items-center justify-between gap-2">
                <div>
                    <p className="text-[0.7rem] uppercase tracking-wide text-gray-400">Live Accounts</p>
                    <h3 className="text-lg font-semibold text-white">Coin Wallet Layout</h3>
                </div>
                <div className="text-xs text-gray-300">Prefixes from config/binance_accounts.json</div>
            </div>
            {readyCoins.length === 0 && (
                <div className="mb-3 rounded-lg border border-amber-900/50 bg-amber-950/30 p-3 text-sm text-amber-200" data-testid="live-account-layout-disabled">
                    No coins with passing live checks. Showing all configured coins and their blocking reasons so an empty check-passing list is not mistaken for success.
                    {enabledCoins.length === 0 ? " No coins are Binance-enabled." : ""}
                </div>
            )}
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-gray-200">
                    <thead>
                        <tr className="text-left text-gray-400">
                            <th className="px-2 py-2">Symbol</th>
                            <th className="px-2 py-2">Coin</th>
                            <th className="px-2 py-2">Account Prefix</th>
                            <th className="px-2 py-2">Env Keys</th>
                            <th className="px-2 py-2">Binance Enabled</th>
                            <th className="px-2 py-2">Can Trade Live</th>
                            <th className="px-2 py-2">Blocking</th>
                        </tr>
                    </thead>
                    <tbody>
                        {layout.map((item, index) => {
                            const symbol = item?.symbol || getCoinName(item);
                            const coinKey = item?.coin_key || item?.slug || "unknown";
                            const prefix = item?.binance_account_prefix;
                            const envKeys = item?.env_keys_present;
                            const canTrade = item?.can_trade_live === true;
                            const enabled = item?.binance_enabled === true;
                            const envBadge = <EnvBadge status={envKeys} />;
                            const blocking = getItemReasons(item);
                            return (
                                <tr key={`${symbol}-${prefix || coinKey}-${index}`} className="border-t border-gray-800">
                                    <td className="px-2 py-2 font-semibold text-white">{symbol}</td>
                                    <td className="px-2 py-2 text-xs text-gray-400">{coinKey}</td>
                                    <td className="px-2 py-2">{prefix || "-"}</td>
                                    <td className="px-2 py-2">{envBadge}</td>
                                    <td className="px-2 py-2">{enabled ? <span className="text-green-300">Yes</span> : <span className="text-amber-300">No — Binance disabled</span>}</td>
                                    <td className="px-2 py-2">{canTrade ? <span className="text-green-300">Yes</span> : <span className="text-amber-300">No — checks blocked</span>}</td>
                                    <td className="px-2 py-2 text-xs text-gray-300">{blocking.length ? blocking.map(formatReason).join(", ") : "None"}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

LiveAccountLayoutPanel.propTypes = {
    readiness: PropTypes.object,
    className: PropTypes.string,
};
